import { useContext } from 'react'
import { createStore, type StoreApi, useStore } from 'zustand'

import { useReconciliationEntitled } from '@/modules/account'
import {
  useAllTransactions,
  useLoading,
  useStatements,
} from '@/modules/projects/hooks/project'
import type {
  LedgerIncomplete,
  ProjectStore,
} from '@/modules/projects/stores/project/types'
import type { Statement, Transaction } from '@/modules/projects/stores/types'
import { ProjectContext } from '../ProjectStoreContext'
import {
  buildPortfolioOverview,
  type PortfolioOverview,
  shareOverviewIndex,
} from './model/portfolioOverview'
import { foldSecuritiesAccounts } from './useProjectSecurities'

type OverviewsByPlan = Map<boolean, PortfolioOverview>

const overviewsByStatements = new WeakMap<
  Statement[],
  WeakMap<Transaction[], OverviewsByPlan>
>()

const overviewsByPlanFor = (
  statements: Statement[],
  transactions: Transaction[],
): OverviewsByPlan => {
  const byTransactions =
    overviewsByStatements.get(statements) ??
    new WeakMap<Transaction[], OverviewsByPlan>()
  overviewsByStatements.set(statements, byTransactions)
  const byPlan = byTransactions.get(transactions) ?? new Map()
  byTransactions.set(transactions, byPlan)
  return byPlan
}

// The store hands back the same arrays until the data changes, so they key the cache with the plan
// flag. A re-render with the same data reuses the overview; a new statement or ledger page builds it once.
export const portfolioOverviewFor = (
  statements: Statement[],
  transactions: Transaction[],
  cashInPlan: boolean,
): PortfolioOverview => {
  const byPlan = overviewsByPlanFor(statements, transactions)
  const cached = byPlan.get(cashInPlan)
  if (cached) return cached
  const overview = buildPortfolioOverview(
    foldSecuritiesAccounts(statements),
    statements,
    transactions,
    cashInPlan,
  )
  byPlan.set(cashInPlan, overview)
  return overview
}

// The model plus whether the cash ledger it sums is short, so a money figure can say so.
export type PortfolioOverviewWithLedger = PortfolioOverview & {
  ledgerIncomplete: LedgerIncomplete | null
}

const withLedgerByOverview = new WeakMap<
  PortfolioOverview,
  PortfolioOverviewWithLedger
>()

// Kept per overview so month and view caches keyed on the object still hit between renders.
export const withLedger = (
  overview: PortfolioOverview,
  ledgerIncomplete: LedgerIncomplete | null,
): PortfolioOverviewWithLedger => {
  const cached = withLedgerByOverview.get(overview)
  if (cached?.ledgerIncomplete === ledgerIncomplete) return cached
  const composed = { ...overview, ledgerIncomplete }
  shareOverviewIndex(overview, composed)
  withLedgerByOverview.set(overview, composed)
  return composed
}

// Outside a project (a list rendered on its own) there is no ledger to be short.
const NO_PROJECT_STORE = createStore<ProjectStore>(
  () => ({ ledgerIncomplete: null }) as unknown as ProjectStore,
)

const useProjectStoreApi = (): StoreApi<ProjectStore> =>
  useContext(ProjectContext)?.projectStore ?? NO_PROJECT_STORE

export interface LedgerGap {
  ledgerIncomplete: LedgerIncomplete | null
  retry: () => Promise<void>
}

export const useLedgerGap = (): LedgerGap => {
  const store = useProjectStoreApi()
  const ledgerIncomplete = useStore(
    store,
    (state) => state.ledgerIncomplete ?? null,
  )
  return {
    ledgerIncomplete,
    retry: () => store.getState().loaders.retryFailedLedgerPage(),
  }
}

const lastBuiltByStore = new WeakMap<
  StoreApi<ProjectStore>,
  PortfolioOverviewWithLedger
>()

// The whole Portfolio model: accounts over time, the merged Activity list and the total series.
// Read a month with accountsAsOf(overview, month); read the list with activityViewFor(overview.activity, query).
// Built once a load ends, never per ledger page (one build is seconds on a 240k-row case). While
// pages arrive it is the last one built, with the gap it was built with, so a retry keeps the page
// up; null until the first load ends.
export const usePortfolioOverview = (): PortfolioOverviewWithLedger | null => {
  const store = useProjectStoreApi()
  const statements = useStatements()
  const transactions = useAllTransactions()
  const cashInPlan = useReconciliationEntitled()
  const isLoading = useLoading()
  const { ledgerIncomplete } = useLedgerGap()

  if (isLoading) return lastBuiltByStore.get(store) ?? null
  const overview = withLedger(
    portfolioOverviewFor(statements, transactions, cashInPlan),
    ledgerIncomplete,
  )
  lastBuiltByStore.set(store, overview)
  return overview
}
